import { useState, useEffect, useCallback } from 'react';

const getViewport = () => ({ w: window.innerWidth, h: window.innerHeight });

// Best column count so that n 16:9 tiles fill the viewport with the largest possible tiles.
export const bestColumns = (n, w, h) => {
    let best = 1;
    let bestWidth = 0;
    for (let cols = 1; cols <= n; cols++) {
        const rows = Math.ceil(n / cols);
        const tileW = Math.min(w / cols, (h / rows) * (16 / 9));
        if (tileW > bestWidth) {
            bestWidth = tileW;
            best = cols;
        }
    }
    return best;
};

// Wall display mode: full-viewport multiview, browser fullscreen when available, screen kept awake.
export const useWallMode = () => {
    const [isWall, setIsWall] = useState(false);
    const [viewport, setViewport] = useState(getViewport);

    const enter = useCallback(() => {
        setIsWall(true);
        document.documentElement.requestFullscreen?.().catch(() => {});
    }, []);

    const exit = useCallback(() => {
        setIsWall(false);
        if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    }, []);

    useEffect(() => {
        if (!isWall) return undefined;
        const onResize = () => setViewport(getViewport());
        const onFullscreenChange = () => {
            if (!document.fullscreenElement) setIsWall(false);
        };
        onResize();
        window.addEventListener('resize', onResize);
        document.addEventListener('fullscreenchange', onFullscreenChange);

        let lock = null;
        let cancelled = false;
        const acquire = async () => {
            try {
                const l = await navigator.wakeLock?.request('screen');
                if (cancelled) l?.release();
                else lock = l || null;
            } catch { /* wake lock unavailable or denied */ }
        };
        const onVisible = () => { if (document.visibilityState === 'visible') acquire(); };
        acquire();
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            cancelled = true;
            window.removeEventListener('resize', onResize);
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('visibilitychange', onVisible);
            lock?.release();
        };
    }, [isWall]);

    return { isWall, enter, exit, viewport };
};
